import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ActionMenu, type ActionMenuItem } from '@fiado/react-kit';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { PlatformRoleForm } from './PlatformRoleForm';
import type { PlatformRoleListItemLocal } from '../../../api/types/PlatformLocal';

interface Props {
    role: PlatformRoleListItemLocal;
}

/**
 * Menú "⋮" de acciones por fila de rol PLATFORM. Editar/eliminar van contra `platformApi.*PlatformRole`,
 * gateados por `platform.role.update`/`platform.role.delete` (scope PLATFORM). El borrado pide
 * confirmación. (Los roles de sistema —isSystem— NO renderizan este menú: lo decide la tabla, que solo
 * monta RowActions para roles custom.)
 *
 * Usa el <ActionMenu> del kit: items declarativos con `permission`/`scope`; el kit los filtra con
 * usePermissions() (alimentado por PermissionsBridge → useAuth) y NO renderiza el "⋮" si no queda
 * ningún item visible (reemplaza el guard manual ACTION_PERMS.some + hasScopeAccess). Los dialogs
 * siguen aquí: el onSelect del item los abre.
 */
export function PlatformRoleRowActions({ role }: Props) {
    const qc = useQueryClient();
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['platform', 'roles'] });

    const deleteMut = useMutation({
        mutationFn: () => platformApi.deletePlatformRole(role.roleId),
        onSuccess: async () => {
            toast.success('Rol eliminado');
            setConfirmDelete(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    const items: ActionMenuItem[] = [
        {
            label: 'Editar',
            permission: 'platform.role.update',
            scope: 'PLATFORM',
            onSelect: () => {
                setEditOpen(true);
            },
        },
        {
            label: 'Eliminar',
            permission: 'platform.role.delete',
            scope: 'PLATFORM',
            destructive: true,
            onSelect: () => {
                setConfirmDelete(true);
            },
        },
    ];

    return (
        <>
            <ActionMenu items={items} />

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Eliminar rol"
                description={`El rol "${role.displayName}" se eliminará. Los usuarios que lo tengan asignado perderán esos permisos. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Editar: modal plano (mismo patrón que la tabla de crear). */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setEditOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Editar rol</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setEditOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformRoleForm
                            role={role}
                            onDone={() => {
                                setEditOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
